/**
 * Distribution service — MOCK-FIRST (CT-3900, scoped as Distribution).
 *
 * Where each connected integration is exposed: per studio (Governance / Agentic /
 * Workforce) and per workspace. A single mutable seed powers both the
 * integration-first view (CT-4206/4207/4208) and the workspace-first view
 * (CT-4209). Platform envelope, snake_case, `TODO(CT-3904 / backend)`.
 */

/**
 * Prototype org-workspaces directory (CT-4208/4209). Mock stand-in for a real
 * workspaces service — mirrors the Access directory (instanceService) so member
 * counts/colors don't drift. TODO(workspaces directory / backend).
 */
export const WORKSPACE_DIRECTORY = [
  { id: 'sales', name: 'Sales Team', initials: 'ST', color: '#06B6D4', avatarClassName: 'bg-[#06B6D4]', member_count: 11, desc: 'Pipeline + outbound', owner: 'María González' },
  { id: 'eng', name: 'Engineering', initials: 'E', color: '#A78BFA', avatarClassName: 'bg-[#A78BFA]', member_count: 12, desc: 'Product + platform', owner: 'Edgardo Sierra' },
  { id: 'cs', name: 'Customer Success', initials: 'CS', color: '#FDC700', avatarClassName: 'bg-[#FDC700]', member_count: 7, desc: 'Support + onboarding', owner: 'Sarah Chen' },
  { id: 'fin', name: 'Finance', initials: 'F', color: '#10B981', avatarClassName: 'bg-[#10B981]', member_count: 4, desc: 'Billing + reporting', owner: 'Alex Park' },
  { id: 'mkt', name: 'Marketing', initials: 'M', color: '#EF4444', avatarClassName: 'bg-[#EF4444]', member_count: 6, desc: 'Campaigns + lifecycle', owner: 'Thomas González' },
];

/** Workspace names, in directory order (kept for the workspace-first view / seed). */
export const WORKSPACES = WORKSPACE_DIRECTORY.map((w) => w.name);

const ALL = { governance: true, agentic: true, workforce: true };
const NONE = { governance: false, agentic: false, workforce: false };

const buildSeed = () => [
  row('slack', 'Slack', {
    vendor: 'Slack Technologies',
    owner: 'María González',
    status: 'working',
    studios: { governance: true, agentic: true, workforce: false },
    workspaces_mode: 'all',
    surfaces: { widgets: true, assistant: true },
  }),
  row('salesforce', 'Salesforce', {
    vendor: 'Salesforce.com',
    owner: 'Danilo Petrov',
    status: 'working',
    studios: { governance: true, agentic: true, workforce: false },
    workspaces_mode: 'selected',
    workspaces: ['Sales Team', 'Marketing'],
    surfaces: { widgets: true, assistant: false },
  }),
  row('github', 'GitHub', {
    vendor: 'GitHub Inc.',
    owner: 'Elena Soto',
    status: 'schema_drift',
    studios: { governance: false, agentic: true, workforce: false },
    workspaces_mode: 'selected',
    workspaces: ['Engineering'],
    surfaces: { widgets: false, assistant: null },
  }),
  row('notion', 'Notion', {
    vendor: 'Notion Labs',
    owner: 'María González',
    status: 'working',
    studios: { governance: true, agentic: false, workforce: false },
    workspaces_mode: 'all',
    surfaces: { widgets: true, assistant: true },
  }),
  row('snowflake', 'Snowflake', {
    vendor: 'Snowflake Inc.',
    owner: 'Danilo Petrov',
    status: 'working',
    studios: { governance: true, agentic: true, workforce: false },
    workspaces_mode: 'selected',
    workspaces: ['Finance'],
    surfaces: { widgets: false, assistant: false },
  }),
  row('jira', 'Jira', {
    vendor: 'Atlassian',
    owner: 'Elena Soto',
    status: 'working',
    studios: { governance: true, agentic: true, workforce: true },
    workspaces_mode: 'selected',
    workspaces: ['Engineering', 'Customer Success'],
    surfaces: { widgets: true, assistant: true },
  }),
  row('zendesk', 'Zendesk', {
    vendor: 'Zendesk',
    owner: 'María González',
    status: 'auth_failed',
    studios: { governance: true, agentic: false, workforce: true },
    workspaces_mode: 'all',
    surfaces: { widgets: true, assistant: null },
  }),
  row('datadog', 'Datadog', {
    vendor: 'Datadog, Inc.',
    owner: 'Elena Soto',
    status: 'rate_limited',
    studios: { governance: false, agentic: true, workforce: false },
    workspaces_mode: 'selected',
    workspaces: ['Engineering'],
    surfaces: { widgets: false, assistant: false },
  }),
  row('google-drive', 'Google Drive', {
    vendor: 'Google',
    owner: 'Danilo Petrov',
    status: 'working',
    studios: ALL,
    workspaces_mode: 'all',
    surfaces: { widgets: true, assistant: true },
  }),
  row('linear', 'Linear', {
    vendor: 'Linear',
    owner: 'María González',
    status: 'working',
    studios: NONE,
    workspaces_mode: 'selected',
    workspaces: [],
    surfaces: { widgets: null, assistant: null },
  }),
  row('amazon-s3', 'Amazon S3', {
    vendor: 'Amazon Web Services',
    owner: 'Elena Soto',
    status: 'working',
    studios: { governance: true, agentic: false, workforce: false },
    workspaces_mode: 'selected',
    workspaces: ['Engineering', 'Finance'],
    surfaces: { widgets: false, assistant: false },
  }),
  row('hubspot', 'HubSpot', {
    vendor: 'HubSpot, Inc.',
    owner: 'Danilo Petrov',
    status: 'working',
    studios: { governance: true, agentic: true, workforce: false },
    workspaces_mode: 'all',
    surfaces: { widgets: true, assistant: true },
  }),
];

function row(slug, name, extra = {}) {
  const studios = extra.studios ?? NONE;
  const surfaces = extra.surfaces ?? {};
  return {
    slug,
    name,
    vendor: extra.vendor ?? name,
    owner: extra.owner ?? '',
    status: extra.status ?? 'working',
    studios,
    workspaces_mode: extra.workspaces_mode ?? 'all',
    workspaces: extra.workspaces ?? [],
    surfaces: {
      data_studio: studios.governance,
      agent_studio: studios.agentic,
      workflows: studios.workforce,
      widgets: surfaces.widgets ?? null,
      assistant: surfaces.assistant ?? null,
    },
    updated_at: '2026-08-01T10:00:00.000Z',
  };
}

let mockDistribution = buildSeed();

export const __resetMockDistributionForTests = () => {
  mockDistribution = buildSeed();
};

const envelope = (body, meta = {}) => ({ status_code: 200, body, meta });
const find = (slug) => mockDistribution.find((d) => d.slug === slug);

/** All connected integrations with their distribution settings (CT-4206). */
export const listDistribution = () =>
  Promise.resolve(envelope(mockDistribution, { total: mockDistribution.length }));

/** One integration's distribution settings (CT-4207/4208). */
export const getDistribution = (slug) => Promise.resolve(envelope(find(slug) ?? null));

/** Enable/disable a studio for an integration (CT-4207). TODO(CT-3904 / backend). */
export const setStudioExposure = (slug, studio, enabled) => {
  const d = find(slug);
  if (d) {
    d.studios = { ...d.studios, [studio]: enabled };
    d.surfaces = {
      ...d.surfaces,
      data_studio: d.studios.governance,
      agent_studio: d.studios.agentic,
      workflows: d.studios.workforce,
    };
  }
  return Promise.resolve(envelope(d));
};

/** Impact of disabling a studio (CT-4207). */
export const getStudioDisableImpact = (slug, studio) =>
  Promise.resolve(envelope({ studio, affected: studio === 'agentic' ? 4 : 1 }));

// The three studio-backed surfaces mirror the studio flags so Provider Overview
// "Used in Studios" (CT-4179) stays consistent; widgets/assistant are surface-only.
const STUDIO_BY_SURFACE = { data_studio: 'governance', agent_studio: 'agentic', workflows: 'workforce' };
// Prototype `STUDIO_USAGE` (Full vision `#/distribution`) — drives the hide-modal
// impact list. Empty usage → the box still renders, with the "no tracked
// consumers" copy. TODO(CT-3904 / backend).
const STUDIO_USAGE = {
  slack: { agentic: { count: 47, unit: 'calls/day', detail: '3 agents' }, workforce: { count: 12, unit: 'channels', detail: '4 routes' } },
  github: { agentic: { count: 23, unit: 'calls/day', detail: '2 agents' }, governance: { count: 340, unit: 'docs indexed', detail: 'updated 2h ago' } },
  jira: { agentic: { count: 18, unit: 'calls/day', detail: '1 agent' }, workforce: { count: 2, unit: 'channels', detail: '1 route' } },
  snowflake: { agentic: { count: 8, unit: 'queries/day', detail: '1 agent' }, governance: { count: 1247, unit: 'tables', detail: 'updated 4h ago' } },
  'google-drive': { agentic: { count: 64, unit: 'calls/day', detail: '4 agents' }, governance: { count: 1820, unit: 'files indexed', detail: 'updated 10m ago' } },
  notion: { governance: { count: 234, unit: 'pages', detail: 'updated 1h ago' }, agentic: { count: 12, unit: 'calls/day', detail: '1 agent' } },
  salesforce: { agentic: { count: 34, unit: 'calls/day', detail: '2 agents' }, workforce: { count: 5, unit: 'channels', detail: '2 routes' } },
  zendesk: { agentic: { count: 0, unit: 'calls/day' }, workforce: { count: 0, unit: 'channels' } },
  linear: { agentic: { count: 9, unit: 'calls/day', detail: '1 agent' } },
  datadog: { agentic: { count: 2, unit: 'triggers/day', detail: '1 agent' }, workforce: { count: 1, unit: 'channels', detail: '1 route' } },
  hubspot: { agentic: { count: 11, unit: 'calls/day', detail: '1 agent' }, workforce: { count: 3, unit: 'channels', detail: '1 route' } },
};

// Prototype `MOCK_WIDGET_TEMPLATES` — only `installed` widgets appear as consumers.
const INSTALLED_WIDGETS = [
  { name: 'Pipeline Health', integrations: ['salesforce', 'hubspot'] },
  { name: 'CS SLA Tracker', integrations: ['zendesk', 'salesforce'] },
  { name: 'Engineering Velocity', integrations: ['github', 'linear'] },
];

/** Surface-scoped impact, mirroring prototype `_distImpact`. */
const consumersFor = (slug, surface) => {
  const usage = STUDIO_USAGE[slug] || {};
  if (surface === 'widgets') {
    return INSTALLED_WIDGETS.filter((w) => w.integrations.includes(slug)).map((w) => ({
      kind: 'widget',
      label: w.name,
      descKey: 'widgetNamed',
    }));
  }
  if (surface === 'agent_studio' && usage.agentic?.count > 0) {
    return [{ kind: 'agents', label: usage.agentic.detail || `${usage.agentic.count} agents`, descKey: 'agents' }];
  }
  if (surface === 'assistant' && usage.agentic?.count > 0) {
    return [{
      kind: 'assistants',
      label: 'AI Assistant',
      descKey: 'assistants',
      count: usage.agentic.count,
      unit: usage.agentic.unit,
    }];
  }
  if (surface === 'workflows' && usage.workforce?.count > 0) {
    return [{ kind: 'workflows', label: usage.workforce.detail || `${usage.workforce.count} routes`, descKey: 'workflows' }];
  }
  if (surface === 'data_studio' && usage.governance?.count > 0) {
    return [{ kind: 'dashboards', label: usage.governance.detail || `${usage.governance.count} records`, descKey: 'dashboards' }];
  }
  return [];
};

/**
 * Enable/disable a single surface for an integration (CT-4207). `reason` is the
 * governed-change justification — recorded in the audit log by the real backend.
 * TODO(CT-3904 / backend): persist + write the audit entry with `reason`.
 */
export const setSurfaceExposure = (slug, surface, enabled, reason = '') => {
  const d = find(slug);
  if (d) {
    d.surfaces = { ...d.surfaces, [surface]: enabled };
    const studio = STUDIO_BY_SURFACE[surface];
    if (studio) d.studios = { ...d.studios, [studio]: enabled };
    void reason; // audit seam — no-op in the V1 mock
  }
  return Promise.resolve(envelope(d));
};

/** Impact of disabling a surface (CT-4207) — consumers that stop working (mock). */
export const getSurfaceDisableImpact = (slug, surface) =>
  Promise.resolve(envelope({ surface, consumers: consumersFor(slug, surface) }));

/**
 * One integration's workspace visibility (CT-4208): `all` = every workspace,
 * `selected` = only the listed names. Read model for the detail widget.
 */
export const getWorkspaceVisibility = (slug) => {
  const d = find(slug);
  return Promise.resolve(
    envelope({ mode: d?.workspaces_mode ?? 'all', workspaces: d?.workspaces ?? [] }),
  );
};

/** Set workspace visibility (CT-4208 / CT-4209). TODO(CT-3904 / backend). */
export const setWorkspaceVisibility = (slug, mode, workspaces = []) => {
  const d = find(slug);
  if (d) {
    d.workspaces_mode = mode;
    d.workspaces = mode === 'selected' ? workspaces : [];
  }
  return Promise.resolve(envelope(d));
};

/** Toggle a single workspace's inclusion for an integration (CT-4209 workspace-first). */
export const setWorkspaceIncluded = (slug, workspace, included) => {
  const d = find(slug);
  if (d) {
    if (d.workspaces_mode !== 'selected') {
      d.workspaces_mode = 'selected';
      d.workspaces = included ? WORKSPACES.filter((w) => w !== workspace).concat(workspace) : WORKSPACES.filter((w) => w !== workspace);
    } else {
      d.workspaces = included
        ? [...new Set([...d.workspaces, workspace])]
        : d.workspaces.filter((w) => w !== workspace);
    }
  }
  return Promise.resolve(envelope(d));
};

/** Workspace-first view: each workspace with the integrations it includes (CT-4209). */
export const listWorkspaceDistribution = () => {
  const rows = WORKSPACE_DIRECTORY.map((ws) => {
    const integrations = mockDistribution.filter(
      (d) => d.workspaces_mode === 'all' || d.workspaces.includes(ws.name),
    );
    return {
      id: ws.id,
      workspace: ws.name,
      desc: ws.desc,
      owner: ws.owner,
      color: ws.color,
      member_count: ws.member_count,
      integrations: integrations.map((d) => ({ slug: d.slug, name: d.name })),
    };
  });
  return Promise.resolve(envelope(rows, { total: rows.length }));
};
